import { DeleteOutline } from '@mui/icons-material';
import { Box, Card, Divider, Grid, IconButton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import AddMoreButton from '../../../FormFields/AddMoreButton';
import FieldContainer from '../../../FormFields/FieldContainer';
import FormDatePickerField from '../../../FormFields/FormDatePickerField';
import FormFileDrop from '../../../FormFields/FormFileDrop';
import FormTextField from '../../../FormFields/FormTextField';
import FormTitle from '../../../FormFields/FormTitle';
import { TRANSPORTATION } from '../../../formQuestionsEnum';
import { formCardStyle, groupedFieldsStyle } from '../../../TransitionStyles';
import { FILE_TYPES } from '../../../formFileTypesEnum';

DriversForm.propTypes = {
  permissions: PropTypes.object,
};

export default function DriversForm({ permissions }) {
  const { control, setValue } = useFormContext();
  const [focusedField, setFocusedField] = useState(null);
  const {
    data: { isCanceled },
  } = useSelector((state) => state.transitions);

  const baseFieldName = TRANSPORTATION.DRIVER_LIST.fieldName;
  const isEditable = permissions?.[TRANSPORTATION.DRIVER_LIST.id];
  const disabled = !isEditable || isCanceled;

  const { fields, append } = useFieldArray({
    control,
    name: baseFieldName,
    keyName: '_id',
  });

  const watchedValues = useWatch({ control, name: baseFieldName }) || [];
  const visibleRows = fields.map((field, idx) => ({ field, idx })).filter(({ idx }) => !watchedValues[idx]?.deleteFlag);

  const handleDelete = (idx) => {
    setValue(`${baseFieldName}[${idx}].deleteFlag`, true, { shouldDirty: true });
  };

  const getIssueDateMax = (idx) => {
    const today = dayjs().startOf('day');
    const expirationDate = watchedValues[idx]?.licenseExpirationDate;

    if (!expirationDate) return today.toDate();

    const dayBeforeExpiration = dayjs(expirationDate).subtract(1, 'day').startOf('day');
    return dayBeforeExpiration.isBefore(today) ? dayBeforeExpiration.toDate() : today.toDate();
  };

  const getExpirationDateMin = (idx) => {
    const issueDate = watchedValues[idx]?.licenseIssueDate;
    return issueDate ? dayjs(issueDate).add(1, 'day').startOf('day').toDate() : null;
  };

  return (
    TRANSPORTATION.DRIVER_LIST.id in permissions && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          Drivers
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid item xs={12} sx={{ mb: 1 }}>
          <FormTitle fieldAttributes={{ questionTitle: 'Driver Information', fieldType: 'title' }} />
        </Grid>

        {visibleRows.map(({ field, idx }) => (
          <Grid container item xs={12} wrap="nowrap" key={field._id} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={visibleRows.length > 1 ? 11.5 : 12}>
              <Box sx={{ backgroundColor: '#f1f3f4cc', ...groupedFieldsStyle }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'First name' }} />
                      <FormTextField
                        fieldName={`${baseFieldName}[${idx}].firstName`}
                        fieldAttributes={{ isEditable }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                    </FieldContainer>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Last name' }} />
                      <FormTextField
                        fieldName={`${baseFieldName}[${idx}].lastName`}
                        fieldAttributes={{ isEditable }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                    </FieldContainer>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'License number' }} />
                      <FormTextField
                        fieldName={`${baseFieldName}[${idx}].licenseNumber`}
                        fieldAttributes={{ isEditable }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Issue Date' }} />
                      <FormDatePickerField
                        fieldName={`${baseFieldName}[${idx}].licenseIssueDate`}
                        fieldAttributes={{ isEditable }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                        maxDate={getIssueDateMax(idx)}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Expiration Date' }} />
                      <FormDatePickerField
                        fieldName={`${baseFieldName}[${idx}].licenseExpirationDate`}
                        fieldAttributes={{ isEditable }}
                        focusedField={focusedField}
                        setFocusedField={setFocusedField}
                        minDate={getExpirationDateMin(idx)}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'License', fieldType: 'file' }} />
                      <FormFileDrop
                        fieldName={`${baseFieldName}[${idx}].driverLicense`}
                        fieldAttributes={{
                          isEditable,
                          fileTypeId: FILE_TYPES.DRIVER_LICENSE,
                        }}
                      />
                    </FieldContainer>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FieldContainer>
                      <FormTitle fieldAttributes={{ questionTitle: 'Clean driving record', fieldType: 'file' }} />
                      <FormFileDrop
                        fieldName={`${baseFieldName}[${idx}].cleanDrivingRecord`}
                        fieldAttributes={{
                          isEditable,
                          fileTypeId: FILE_TYPES.CLEAN_DRIVING_RECORD,
                        }}
                      />
                    </FieldContainer>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {visibleRows.length > 1 && (
              <Grid item xs={0.5}>
                <IconButton onClick={() => handleDelete(idx)} disabled={disabled} color="error">
                  <DeleteOutline />
                </IconButton>
              </Grid>
            )}
          </Grid>
        ))}

        <Grid item xs={12}>
          <AddMoreButton
            buttonLabel="Add more"
            handleAddMoreClick={() =>
              append({
                id: 0,
                firstName: null,
                lastName: null,
                licenseNumber: null,
                licenseIssueDate: null,
                licenseExpirationDate: null,
                driverLicense: [],
                cleanDrivingRecord: [],
              })
            }
            disabled={disabled}
          />
        </Grid>
      </Card>
    )
  );
}
